import {type FeedbackRef, frownIcon, frustratedIcon, grinIcon, smileIcon, UserFeedback} from '@github-ui/user-feedback'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {forwardRef, type RefObject, useImperativeHandle, useRef} from 'react'

import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {type DialogRef, InterviewSurveyDialog} from './InterviewSurveyDialog'

const ratingOptions = [
  {
    name: 'Hate it',
    value: 'VERY_NEGATIVE' as const,
    icon: frustratedIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Not great',
    value: 'NEGATIVE' as const,
    icon: frownIcon,
    color: 'dissatisfied' as const,
  },
  {
    name: 'It’s ok',
    value: 'POSITIVE' as const,
    icon: smileIcon,
    color: 'satisfied' as const,
  },
  {
    name: 'Love it',
    value: 'VERY_POSITIVE' as const,
    icon: grinIcon,
    color: 'verySatisfied' as const,
  },
]

type Rating = (typeof ratingOptions)[number]['value']
export type FeedbackDialogRef = FeedbackRef<Rating>

type UserFeedbackEvent = {
  subject?: string
  rating: Rating | null
  content?: string
  hostname: string
  path?: string
  mode: string
}

type ConversationFeedbackDialogProps = {
  mode: 'assistive' | 'immersive'
  returnFocusRef?: RefObject<HTMLElement>
}

export const ConversationFeedbackDialog = forwardRef(function (
  {mode, returnFocusRef}: ConversationFeedbackDialogProps,
  ref: React.ForwardedRef<FeedbackDialogRef>,
) {
  const surveyDialogRef = useRef<DialogRef>(null)
  const feedbackDialogRef = useRef<FeedbackDialogRef>(null)
  const initialRatingRef = useRef<Rating | undefined>(undefined)

  useImperativeHandle(ref, () => ({
    openDialog: (rating?: Rating) => {
      initialRatingRef.current = rating
      if (copilotFeatureFlags.copilotChatInterviewSurvey) {
        surveyDialogRef.current?.openDialog()
      } else {
        feedbackDialogRef.current?.openDialog(rating)
      }
    },
  }))

  const onSubmit = async (rating: Rating | null, text: string): Promise<string[]> => {
    const errors = []

    if (rating == null) {
      errors.push('Please select a rating.')
    }

    const limit = 2000
    if (text.length > limit) {
      errors.push(`Please keep your feedback within ${limit} characters or less.`)
    }

    if (errors.length > 0) {
      return errors
    }

    const body: UserFeedbackEvent = {
      rating,
      content: text,
      hostname: window.location.hostname,
      path: window.location.pathname,
      mode,
    }

    try {
      const response = await verifiedFetchJSON('/github-copilot/feedback', {method: 'POST', body})
      if (!response.ok) {
        errors.push('An error occurred while submitting your feedback.')
      }
    } catch {
      errors.push('An error occurred while submitting your feedback.')
    }

    return errors
  }

  const onSurveyClose = (reason: 'close' | 'no-thanks') => {
    if (reason === 'no-thanks') {
      feedbackDialogRef.current?.openDialog(initialRatingRef.current)
    }
  }

  return (
    <>
      <InterviewSurveyDialog ref={surveyDialogRef} onClose={onSurveyClose} />
      <UserFeedback
        ref={feedbackDialogRef}
        options={ratingOptions}
        onSubmit={onSubmit}
        returnFocusRef={returnFocusRef}
      />
    </>
  )
})

ConversationFeedbackDialog.displayName = 'ConversationFeedbackDialog'
